import React from "react";
import {
  User,
  Calendar,
  MapPin,
  Handshake,
  Waves,
  CheckCircle2,
  Recycle,
  Flower2,
  Camera,
  PersonStanding,
  Shirt,
  Music2,
  FlaskConical,
  Trophy,
  BookOpen,
  Clock,
  Download,
  FileText,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { arteNaturaEvents, arteNaturaSymbiosi, arteNaturaFiori } from "../mock/mock";

const DISCIPLINE_ICONS = {
  camera: Camera,
  dance: PersonStanding,
  body: Shirt,
  sound: Music2,
  science: FlaskConical,
};

const ArteNatura = () => {
  const { t, pick } = useLang();

  return (
    <div>
      <PageHero
        image="https://customer-assets.emergentagent.com/job_arte-natura-hub/artifacts/35pl6f16_484982536_680468811313463_499751557762737408_n.jpg"
        kicker={t("arteNatura.kicker")}
        title={t("arteNatura.title")}
        lead={t("arteNatura.lead")}
      />

      {/* OVERVIEW */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <Reveal>
            <span className="text-[#b5533a] text-xs uppercase tracking-[0.24em]">
              {t("arteNatura.overviewTitle")}
            </span>
            <p className="mt-5 font-serif text-2xl md:text-[1.9rem] leading-[1.45] text-[#2b2926]">
              {t("arteNatura.overviewP1")}
            </p>
            <p className="mt-6 text-[#4a463f] leading-relaxed">{t("arteNatura.overviewP2")}</p>

            <h3 className="mt-10 font-serif text-2xl md:text-3xl text-[#1c1b1a]">
              {t("arteNatura.objectivesTitle")}
            </h3>
            <ul className="mt-5 space-y-3" data-testid="arte-natura-objectives">
              {(t("arteNatura.objectives") || []).map((o, i) => (
                <li key={i} className="flex gap-3 text-[#4a463f] leading-relaxed">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#b5533a]" />
                  <span>{o}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* FIORI — 3ª edizione (in evidenza) */}
      <section className="bg-[#15211a] py-20 lg:py-28 text-[#e9e4d8]" data-testid="arte-natura-fiori">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative order-2 lg:order-1">
              <img
                src={arteNaturaFiori.heroImage}
                alt="Fiori"
                className="w-full aspect-[4/5] object-cover object-center"
                data-testid="fiori-hero-image"
              />
            </div>
            <div className="order-1 lg:order-2">
              <span className="inline-flex items-center gap-2 text-[#e3a9b6] text-xs uppercase tracking-[0.2em]">
                <Flower2 className="w-4 h-4" /> {pick(arteNaturaFiori.edition)}
              </span>
              <h2 className="mt-3 font-serif text-5xl md:text-6xl text-white leading-none">
                {arteNaturaFiori.title}
              </h2>
              <p className="mt-2 font-serif italic text-2xl text-[#e3a9b6]">
                {pick(arteNaturaFiori.subtitle)}
              </p>
              <p className="mt-3 text-sm uppercase tracking-[0.14em] text-[#9db89f]" data-testid="fiori-partof">
                {pick(arteNaturaFiori.partOf)}
              </p>
              <p className="mt-6 text-[#cbc5b8] leading-relaxed">{pick(arteNaturaFiori.intro)}</p>
              <p className="mt-4 text-[#cbc5b8] leading-relaxed">{pick(arteNaturaFiori.intro2)}</p>
            </div>
          </Reveal>

          {/* Un viaggio tra natura e arte */}
          <Reveal className="mt-16 mb-8">
            <h3 className="font-serif text-3xl md:text-4xl text-white">
              {t("arteNatura.fioriJourneyTitle")}
            </h3>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {arteNaturaFiori.disciplines.map((d, i) => {
              const Icon = DISCIPLINE_ICONS[d.icon] || Flower2;
              return (
                <Reveal key={i} delay={i * 80} className="bg-white/5 border border-white/10 p-7">
                  <Icon className="w-7 h-7 text-[#e3a9b6]" strokeWidth={1.5} />
                  <h4 className="mt-4 font-serif text-xl text-white">{pick(d.title)}</h4>
                  <p className="mt-3 text-sm text-[#cbc5b8] leading-relaxed">{pick(d.text)}</p>
                </Reveal>
              );
            })}
          </div>

          {/* Concorso + Libri */}
          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <Reveal className="bg-[#e3a9b6]/10 border border-[#e3a9b6]/25 p-7">
              <div className="flex items-center gap-3">
                <Trophy className="w-6 h-6 text-[#e3a9b6]" strokeWidth={1.6} />
                <h4 className="font-serif text-2xl text-white">{pick(arteNaturaFiori.contest.title)}</h4>
              </div>
              <p className="mt-3 text-sm text-[#cbc5b8] leading-relaxed">
                {pick(arteNaturaFiori.contest.text)}
              </p>
              <p
                className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-[#e3a9b6] font-medium"
                data-testid="fiori-contest-soon"
              >
                <Clock className="w-4 h-4" /> {pick(arteNaturaFiori.contest.soon)}
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-4">
              <Link
                to="/regolamento-racconta-un-fiore"
                data-testid="fiori-contest-rules-page-link"
                className="inline-flex items-center gap-2 border border-[#e3a9b6] text-[#e3a9b6] hover:bg-[#e3a9b6] hover:text-[#15211a] px-5 py-3 text-xs uppercase tracking-[0.14em] font-medium transition-colors"
              >
                <FileText className="w-4 h-4" /> {pick({ it: "Leggi il regolamento", en: "Read the rules" })}
              </Link>
              <a
                href={arteNaturaFiori.contest.rulesUrl}
                target="_blank"
                rel="noopener noreferrer"
                download
                data-testid="fiori-contest-rules-download"
                className="flex w-fit items-center gap-2 bg-[#e3a9b6] hover:bg-[#d693a3] text-[#15211a] px-5 py-3 text-xs uppercase tracking-[0.14em] font-medium transition-colors"
              >
                <Download className="w-4 h-4" /> {pick(arteNaturaFiori.contest.rulesLabel)}
              </a>
              </div>
            </Reveal>

            <Reveal delay={100} className="bg-white/5 border border-white/10 p-7">
              <div className="flex items-center gap-3 mb-4">
                <BookOpen className="w-6 h-6 text-[#e3a9b6]" strokeWidth={1.6} />
                <h4 className="font-serif text-2xl text-white">{t("arteNatura.fioriBooksTitle")}</h4>
              </div>
              <div className="space-y-4">
                {arteNaturaFiori.books.map((b, i) => (
                  <div key={i}>
                    <p className="text-sm font-medium text-white">{pick(b.title)}</p>
                    <p className="mt-1 text-sm text-[#cbc5b8] leading-relaxed">{pick(b.text)}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Gallery */}
          <div
            className={`mt-6 grid gap-6 ${
              arteNaturaFiori.gallery.length > 1 ? "sm:grid-cols-2" : "grid-cols-1"
            }`}
          >
            {arteNaturaFiori.gallery.map((src, i) => (
              <Reveal
                key={i}
                delay={i * 100}
                className={`overflow-hidden group ${
                  arteNaturaFiori.gallery.length > 1 ? "aspect-[4/3]" : "aspect-[16/9]"
                }`}
              >
                <img
                  src={src}
                  alt={`Fiori ${i + 1}`}
                  className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105"
                />
              </Reveal>
            ))}
          </div>

          {/* Team + Venue + Collab */}
          <div className="mt-10 grid gap-8 lg:grid-cols-3">
            <Reveal className="bg-white/5 border border-white/10 p-7">
              <h4 className="text-xs uppercase tracking-[0.2em] text-[#e3a9b6] mb-4">
                {t("arteNatura.teamTitle")}
              </h4>
              <ul className="space-y-2.5">
                {arteNaturaFiori.team.map((m, i) => (
                  <li key={i} className="text-sm">
                    <span className="text-white">{m.name}</span>
                    <span className="text-[#9a948a]"> — {pick(m.role)}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={100} className="lg:col-span-2 flex flex-col justify-center gap-5">
              <div className="flex items-start gap-2 text-sm text-[#cbc5b8]">
                <MapPin className="w-4 h-4 shrink-0 mt-0.5 text-[#e3a9b6]" />
                <span>
                  <span className="uppercase tracking-[0.12em] text-[#e3a9b6] text-xs">
                    {t("arteNatura.fioriVenueLabel")}:
                  </span>{" "}
                  {pick(arteNaturaFiori.venue)}
                </span>
              </div>
              <div className="flex items-start gap-2 text-sm text-[#cbc5b8]">
                <Handshake className="w-4 h-4 shrink-0 mt-0.5 text-[#e3a9b6]" />
                <span>
                  <span className="uppercase tracking-[0.12em] text-[#e3a9b6] text-xs">
                    {t("arteNatura.collabLabel")}:
                  </span>{" "}
                  {pick(arteNaturaFiori.collab)}
                </span>
              </div>
            </Reveal>
          </div>

          {/* Concept pull-quote */}
          <Reveal className="mt-14 border-t border-white/10 pt-10 max-w-3xl">
            <p className="font-serif italic text-2xl md:text-[1.7rem] leading-[1.5] text-white">
              {pick(arteNaturaFiori.concept)}
            </p>
            <p className="mt-5 text-[#cbc5b8] leading-relaxed">{pick(arteNaturaFiori.finale)}</p>
          </Reveal>
        </div>
      </section>

      {/* SYMBIOSI 2024 */}
      <section className="bg-[#141c20] py-20 lg:py-28 text-[#e8e3d8]">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src={arteNaturaSymbiosi.image}
                alt="Symbiosi"
                className="w-full aspect-[4/5] object-cover"
              />
            </div>
            <div>
              <span className="inline-flex items-center gap-2 text-[#7fb0b8] text-xs uppercase tracking-[0.2em]">
                <Waves className="w-4 h-4" /> {pick(arteNaturaSymbiosi.edition)}
              </span>
              <h2 className="mt-3 font-serif text-5xl md:text-6xl text-white leading-none">
                {arteNaturaSymbiosi.title}
              </h2>
              <p className="mt-2 font-serif italic text-2xl text-[#7fb0b8]">
                {pick(arteNaturaSymbiosi.theme)}
              </p>
              <p className="mt-6 text-[#c8c2b6] leading-relaxed">
                {pick(arteNaturaSymbiosi.intro)}
              </p>
              <p className="mt-4 text-[#c8c2b6] leading-relaxed">
                {pick(arteNaturaSymbiosi.body)}
              </p>
            </div>
          </Reveal>

          <div className="mt-14 grid lg:grid-cols-3 gap-8">
            {/* Sustainability */}
            <Reveal className="bg-white/5 border border-white/10 p-7">
              <Recycle className="w-7 h-7 text-[#7fb0b8]" strokeWidth={1.5} />
              <p className="mt-4 text-sm text-[#c8c2b6] leading-relaxed">
                {pick(arteNaturaSymbiosi.sustainability)}
              </p>
            </Reveal>

            {/* Team */}
            <Reveal delay={100} className="bg-white/5 border border-white/10 p-7">
              <h3 className="text-xs uppercase tracking-[0.2em] text-[#7fb0b8] mb-4">
                {t("arteNatura.teamTitle")}
              </h3>
              <ul className="space-y-2.5">
                {arteNaturaSymbiosi.team.map((m, i) => (
                  <li key={i} className="text-sm">
                    <span className="text-white">{m.name}</span>
                    <span className="text-[#9a948a]"> — {pick(m.role)}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            {/* Activities */}
            <Reveal delay={200} className="bg-white/5 border border-white/10 p-7">
              <h3 className="text-xs uppercase tracking-[0.2em] text-[#7fb0b8] mb-4">
                {t("arteNatura.activitiesTitle")}
              </h3>
              <ul className="space-y-2.5">
                {arteNaturaSymbiosi.activities.map((a, i) => (
                  <li key={i} className="flex gap-2 text-sm text-[#c8c2b6]">
                    <CheckCircle2 className="w-4 h-4 text-[#7fb0b8] shrink-0 mt-0.5" />
                    {pick(a)}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <Reveal className="mt-8 flex items-start gap-2 text-xs text-[#9a948a]">
            <Handshake className="w-4 h-4 shrink-0 mt-0.5 text-[#7fb0b8]" />
            <span>
              <span className="uppercase tracking-[0.12em] text-[#7fb0b8]">
                {t("arteNatura.collabLabel")}:
              </span>{" "}
              {pick(arteNaturaSymbiosi.collab)}
            </span>
          </Reveal>

          <Reveal className="mt-4 flex items-start gap-2 text-sm text-[#c8c2b6]">
            <MapPin className="w-4 h-4 shrink-0 mt-0.5 text-[#7fb0b8]" />
            <span>
              <span className="uppercase tracking-[0.12em] text-[#7fb0b8] text-xs">
                {t("arteNatura.exhibitionsLabel")}:
              </span>{" "}
              {pick(arteNaturaSymbiosi.exhibitions)}
            </span>
          </Reveal>
        </div>
      </section>

      {/* EVENTS */}
      <section className="bg-[#faf7f2] py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="mb-14">
            <span className="text-[#b5533a] text-xs uppercase tracking-[0.2em]">
              {t("arteNatura.eventsSubtitle")}
            </span>
            <h2 className="mt-2 font-serif text-4xl md:text-5xl text-[#1c1b1a]">
              {t("arteNatura.eventsTitle")}
            </h2>
          </Reveal>

          <div className="space-y-16">
            {arteNaturaEvents.map((e, i) => (
              <Reveal
                key={i}
                className={`grid lg:grid-cols-2 gap-10 items-center ${
                  i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="aspect-[4/3] overflow-hidden group">
                  <img
                    src={e.image}
                    alt={pick(e.title)}
                    className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105"
                  />
                </div>
                <div>
                  <span className="inline-block bg-[#b5533a] text-white text-[11px] uppercase tracking-[0.16em] px-3 py-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-4 font-serif text-3xl md:text-4xl text-[#1c1b1a]">
                    {pick(e.title)}
                  </h3>
                  <div className="mt-3 space-y-1.5 text-sm text-[#8a8478]">
                    <p className="inline-flex items-center gap-2">
                      <User className="w-4 h-4" /> {t("arteNatura.artist")}: {e.artist}
                    </p>
                    <p className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 shrink-0" /> {pick(e.date)}
                    </p>
                    <p className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 shrink-0 mt-0.5" /> {pick(e.place)}
                    </p>
                  </div>
                  <p className="mt-5 text-[#3d3a35] leading-relaxed">{pick(e.text)}</p>
                  <div className="mt-4 flex items-start gap-2 text-xs text-[#8a8478] border-t border-[#e6dfd2] pt-4">
                    <Handshake className="w-4 h-4 shrink-0 mt-0.5 text-[#b5533a]" />
                    <span>
                      <span className="uppercase tracking-[0.12em] text-[#b5533a]">
                        {t("arteNatura.collabLabel")}:
                      </span>{" "}
                      {pick(e.collab)}
                    </span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PATROCINIO */}
      <section className="bg-[#1c1b1a] py-14">
        <div className="mx-auto max-w-4xl px-5 lg:px-8 text-center">
          <p className="text-[#c7c0b4] text-sm uppercase tracking-[0.16em]">
            {t("arteNatura.patrocinio")}
          </p>
        </div>
      </section>
    </div>
  );
};

export default ArteNatura;
